/**
 * A hypothesis's assessments as a step chart over time (ADR-0095): colour = verdict, height =
 * confidence, width = how long it held. Compact on a card; with its dates and a legend on the
 * hypothesis page. Each step names its verdict, confidence and date for assistive technology.
 */
import { useTranslation } from 'react-i18next'
import type { AssessmentPoint } from '@/types/analytic-hypothesis'
import { cn } from '@/utils/cn'
import { formatDate } from '@/utils/date'
import { CONFIDENCE_HEIGHT, stripSteps, VERDICT_FILL } from './strip'

const COPY = 'analyticHypotheses'

export interface AssessmentStripProps {
  /** Oldest first, as the server sends them. */
  points: readonly AssessmentPoint[]
  /** Where the last step ends: the closing date, or now. */
  end: string
  size?: 'compact' | 'full'
}

function Legend() {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-3xs text-muted-foreground">
      {(['supported', 'refuted', 'inconclusive'] as const).map(verdict => (
        <span key={verdict} className="inline-flex items-center gap-1">
          <span aria-hidden className={cn('size-2 rounded-sm', VERDICT_FILL[verdict])} />
          {t(`${COPY}.verdict.${verdict}`)}
        </span>
      ))}
      <span>{t(`${COPY}.strip.legendHeight`)}</span>
    </div>
  )
}

export function AssessmentStrip({ points, end, size = 'compact' }: AssessmentStripProps) {
  const { t, i18n } = useTranslation()
  const full = size === 'full'
  if (points.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-md border border-dashed border-border/60 text-3xs text-muted-foreground',
          full ? 'h-16' : 'h-6'
        )}
      >
        {t(`${COPY}.strip.empty`)}
      </div>
    )
  }
  const steps = stripSteps(points, end)
  return (
    <div className="flex flex-col gap-1.5">
      <ol
        aria-label={t(`${COPY}.strip.label`)}
        className={cn(
          'flex items-end gap-px rounded-md border border-border/40 bg-muted/40 p-0.5',
          full ? 'h-16' : 'h-6'
        )}
      >
        {steps.map(({ point, share }) => {
          const step = t(`${COPY}.strip.step`, {
            verdict: t(`${COPY}.verdict.${point.verdict}`),
            confidence: t(`${COPY}.confidence.${point.confidence}`),
            date: formatDate(point.created_at, i18n.language),
          })
          return (
            <li
              key={point.id}
              aria-label={step}
              title={step}
              data-verdict={point.verdict}
              data-confidence={point.confidence}
              className="flex h-full min-w-1 basis-0 items-end"
              style={{ flexGrow: share }}
            >
              <span
                aria-hidden
                className={cn(
                  'w-full rounded-t-sm',
                  VERDICT_FILL[point.verdict],
                  CONFIDENCE_HEIGHT[point.confidence]
                )}
              />
            </li>
          )
        })}
      </ol>
      {full && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-2 text-3xs tabular-nums text-muted-foreground">
            <time dateTime={points[0].created_at}>
              {formatDate(points[0].created_at, i18n.language)}
            </time>
            <span aria-hidden>→</span>
            <time dateTime={end}>{formatDate(end, i18n.language)}</time>
          </div>
          <Legend />
        </div>
      )}
    </div>
  )
}
